import { motion } from "framer-motion";
import { ShieldCheck, HeartHandshake, Quote } from "lucide-react";

const PARAGRAPHS = [
    "Aunque Keypoteca es una marca profesional, detrás de ella estoy yo, acompañándote personalmente en cada paso para que consigas la hipoteca que mejor se adapta a ti.",
    "Fui de los primeros en España en obtener la certificación oficial del Banco de España como intermediario de crédito inmobiliario. Desde entonces, he ayudado a miles de personas a conseguir no solo una hipoteca, sino su hogar.",
    "Porque no se trata solo de firmar un préstamo, sino de acompañarte en una de las decisiones más importantes de tu vida.",
    "Siempre me han definido como una persona cercana, empática y comprometida. Me encanta escuchar a cada cliente, entender su historia y diseñar una solución financiera personalizada. Creo profundamente en el valor que aporto y en la tranquilidad que ofrezco en un proceso que, a veces, puede resultar abrumador.",
    "Cada caso es único. Por eso, en Keypoteca, te acompaño paso a paso para que consigas la hipoteca que realmente se adapta a ti: sin estrés, sin letra pequeña y con total confianza.",
];

export default function About() {
    return (
        <section
            id="quien-esta-detras"
            data-testid="about-section"
            className="bg-[#F5F7FA] py-20 lg:py-28 px-4 sm:px-6 lg:px-8"
        >
            <div className="max-w-7xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    className="relative overflow-hidden rounded-[2.5rem] bg-[#112338] noise-overlay"
                >
                    <div className="absolute inset-0 blueprint-grid" />
                    <div
                        className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full pointer-events-none"
                        style={{
                            background:
                                "radial-gradient(circle at center, rgba(16,185,129,0.12) 0%, transparent 70%)",
                        }}
                    />

                    <div className="relative grid lg:grid-cols-12 gap-12 p-8 sm:p-12 lg:p-16 items-center">
                        <div className="lg:col-span-5">
                            <motion.div
                                initial={{ opacity: 0, scale: 0.94 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.7, delay: 0.1 }}
                                className="rounded-3xl border border-white/15 bg-white/5 backdrop-blur overflow-hidden text-center"
                            >
                                <img
                                    src="/assets/santiago.jpg"
                                    alt="Santiago Ferrer, fundador de Keypoteca"
                                    data-testid="about-photo"
                                    className="w-full aspect-[4/5] object-cover object-top"
                                />
                                <div className="p-8 pt-6">
                                <h3 className="font-display text-2xl font-bold text-white">
                                    Santiago Ferrer
                                </h3>
                                <p className="mt-1.5 text-sm text-slate-300">
                                    Fundador y asesor principal de Keypoteca
                                </p>
                                <div
                                    data-testid="about-certification-badge"
                                    className="mt-6 flex items-start gap-3 rounded-2xl bg-emerald-500/10 border border-emerald-400/25 px-4 py-3.5 text-left"
                                >
                                    <ShieldCheck
                                        size={20}
                                        className="text-emerald-400 shrink-0 mt-0.5"
                                    />
                                    <span className="text-[13px] leading-snug text-emerald-100/90">
                                        Certificación oficial del Banco de
                                        España como intermediario de crédito
                                        inmobiliario
                                    </span>
                                </div>
                                <div className="mt-4 flex items-start gap-3 rounded-2xl bg-white/5 border border-white/10 px-4 py-3.5 text-left">
                                    <HeartHandshake
                                        size={20}
                                        className="text-emerald-400 shrink-0 mt-0.5"
                                    />
                                    <span className="text-[13px] leading-snug text-slate-300">
                                        Miles de personas acompañadas hasta
                                        conseguir su hogar
                                    </span>
                                </div>
                                </div>
                            </motion.div>
                        </div>

                        <div className="lg:col-span-7">
                            <motion.div
                                initial={{ opacity: 0, y: 32 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.7, delay: 0.15 }}
                            >
                                <p className="font-mono text-xs uppercase tracking-[0.25em] text-emerald-400">
                                    Quién está detrás
                                </p>
                                <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white text-balance">
                                    Hola, soy Santiago Ferrer, fundador y
                                    asesor principal de Keypoteca, y un
                                    apasionado del mundo hipotecario.
                                </h2>

                                <div className="mt-7 space-y-5 text-slate-300 leading-relaxed text-[15px] sm:text-base">
                                    {PARAGRAPHS.map((p, i) => (
                                        <motion.p
                                            key={i}
                                            initial={{ opacity: 0, y: 16 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            viewport={{
                                                once: true,
                                                margin: "-40px",
                                            }}
                                            transition={{
                                                duration: 0.55,
                                                delay: 0.1 + i * 0.06,
                                            }}
                                        >
                                            {p}
                                        </motion.p>
                                    ))}
                                </div>

                                <motion.div
                                    initial={{ opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6, delay: 0.3 }}
                                    className="mt-9 flex items-start gap-4 border-l-2 border-emerald-400 pl-5"
                                >
                                    <Quote
                                        size={22}
                                        className="text-emerald-400 shrink-0 mt-1"
                                        fill="currentColor"
                                        strokeWidth={0}
                                    />
                                    <p
                                        data-testid="about-signoff"
                                        className="font-display text-lg sm:text-xl font-semibold text-white leading-snug"
                                    >
                                        Tu sueño comienza aquí. Y yo estaré
                                        contigo para ayudarte a hacerlo
                                        realidad.
                                    </p>
                                </motion.div>
                            </motion.div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
